/**
 * Speed Role - Convert Pillar
 *
 * Conversão entre unidades de velocidade usando arquitetura hub-and-spoke.
 * Base: meter_per_second (m/s)
 *
 * @example
 * import { convertSpeed, toBaseSpeed, fromBaseSpeed } from '@attrx/role-morphic/speed/convert';
 *
 * convertSpeed('kilometer_per_hour', 'mile_per_hour', 100);  // 62.1371...
 * toBaseSpeed('kilometer_per_hour', 100);                    // 27.7778...
 * fromBaseSpeed('mile_per_hour', 27.7778);                   // ~62.14
 */

import { SPEED_UNITS, type SpeedUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const SPEED_BASE: SpeedUnit = "meter_per_second";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (meter_per_second)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em meter_per_second
 *
 * @example
 * toBaseSpeed('kilometer_per_hour', 100);  // 27.7778...
 * toBaseSpeed('mile_per_hour', 60);        // 26.8224
 */
export function toBaseSpeed(variant: SpeedUnit, value: number): number {
  const unit = SPEED_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown speed variant: ${variant}`);
  }

  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (meter_per_second) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em meter_per_second
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseSpeed('kilometer_per_hour', 27.7778);  // ~100
 * fromBaseSpeed('mile_per_hour', 26.8224);       // ~60
 */
export function fromBaseSpeed(variant: SpeedUnit, baseValue: number): number {
  const unit = SPEED_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown speed variant: ${variant}`);
  }

  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de velocidade
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertSpeed('kilometer_per_hour', 'mile_per_hour', 100);  // 62.1371...
 * convertSpeed('knot', 'kilometer_per_hour', 1);             // 1.852
 * convertSpeed('mach', 'kilometer_per_hour', 1);             // 1225.044
 */
export function convertSpeed(
  from: SpeedUnit,
  to: SpeedUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base → to
  const baseValue = toBaseSpeed(from, value);
  return fromBaseSpeed(to, baseValue);
}

/**
 * Versão safe de convertSpeed que retorna Result
 *
 * @example
 * tryConvertSpeed('kilometer_per_hour', 'mile_per_hour', 100);
 * // { ok: true, value: 62.1371... }
 *
 * tryConvertSpeed('invalid', 'mile_per_hour', 100);
 * // { ok: false, error: 'Unknown speed variant: invalid' }
 */
export function tryConvertSpeed(
  from: SpeedUnit,
  to: SpeedUnit,
  value: number
): Result<number> {
  try {
    const result = convertSpeed(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasSpeedVariant(variant: string): variant is SpeedUnit {
  return variant in SPEED_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getSpeedVariants(): SpeedUnit[] {
  return Object.keys(SPEED_UNITS) as SpeedUnit[];
}
